import { css, styled, keyframes } from "@pigment-css/react";
import { clsx } from "clsx";
import type { ManagementDataTableProps, Row, RowGrouping } from "./types";
import { CellValue } from "./ui/CellValue";
import { LoadMoreButton } from "./ui/LoadMoreButton";
import { SortIcon } from "./ui/SortIcon";

const shimmer = keyframes({
  "0%": { backgroundPosition: "-200px 0" },
  "100%": { backgroundPosition: "200px 0" },
});

const SkeletonElement = styled("div")(({ theme }) => ({
  borderRadius: theme.managementView.radius[10],
  background: `linear-gradient(90deg, ${theme.colors.neutral[200]} 0%, ${theme.colors.neutral[100]} 50%, ${theme.colors.neutral[200]} 100%)`,
  backgroundSize: "400px 100%",
  animation: `${shimmer} 1.5s ease-in-out infinite`,
}));

const CellSkeleton = styled(SkeletonElement)({
  height: "17px",
});

const TableContainer = styled("div")(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.colors.neutral[0],
  borderRadius: theme.managementView.radius[16],
  overflow: "hidden",
}));

const Table = styled("table")(({ theme }) => ({
  width: "100%",
  borderCollapse: "collapse",
  tableLayout: "fixed",
  fontFamily: theme.managementView.font.family,
}));

const TableHeader = styled("thead")(({ theme }) => ({
  borderBottom: `1px solid ${theme.managementView.colors.neutral[8]}`,
}));

const TableHeaderRow = styled("tr")({});

const TableHeaderCell = styled("th")(({ theme }) => ({
  padding: `${theme.managementView.spacing[16]} ${theme.managementView.spacing[32]}`,
  textAlign: "left",
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[500],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[56],
  cursor: "default",
  userSelect: "none",
  position: "relative",
  whiteSpace: "nowrap",
}));

const sortableHeaderCell = css(({ theme }) => ({
  cursor: "pointer",
  "&:hover": {
    color: theme.managementView.colors.neutral[90],
  },
}));

const TableBody = styled("tbody")({});

const GroupHeaderRow = styled("tr")(({ theme }) => ({
  borderTop: `1px solid ${theme.managementView.colors.neutral[8]}`,
}));

const GroupHeaderCell = styled("th")(({ theme }) => ({
  padding: `${theme.managementView.spacing[24]} ${theme.managementView.spacing[32]} ${theme.managementView.spacing[16]}`,
  textAlign: "left",
  fontWeight: "inherit",
}));

const GroupLabel = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  height: "36px",
  padding: `0 ${theme.managementView.spacing[16]}`,
  marginLeft: `-${theme.managementView.spacing[16]}`,
  borderRadius: theme.managementView.radius[32],
  backgroundColor: theme.managementView.colors.neutral[4],
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[500],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[64],
  whiteSpace: "nowrap",
}));

const TableRow = styled("tr")(({ theme }) => ({
  borderBottom: `1px solid ${theme.managementView.colors.neutral[8]}`,
  transition: "background-color 0.15s",

  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },

  "&:last-child": {
    borderBottom: "none",
  },
}));

const TableCell = styled("td")(({ theme }) => ({
  padding: `${theme.managementView.spacing[16]} ${theme.managementView.spacing[32]}`,
  fontFamily: theme.managementView.font.family,
  verticalAlign: "top",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

// String/text column style
const stringCell = css(({ theme }) => ({
  fontSize: theme.managementView.font.size[17],
  fontWeight: theme.managementView.font.weight[440],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[90],
}));

// Number column style
const numberCell = css(({ theme }) => ({
  fontSize: theme.managementView.font.size[16],
  fontWeight: theme.managementView.font.weight[440],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[90],
}));

interface RowGroup {
  key: string | null;
  rows: Row[];
}

function toGroups(rows: Row[], grouping: RowGrouping | undefined): [RowGroup, ...RowGroup[]] {
  if (!grouping || rows.length === 0) return [{ key: null, rows }];

  const [first, ...rest] = rows;
  const groups: [RowGroup, ...RowGroup[]] = [{ key: grouping.groupOf(first), rows: [first] }];
  for (const row of rest) {
    const key = grouping.groupOf(row);
    const last = groups[groups.length - 1];
    if (last.key === key) last.rows.push(row);
    else groups.push({ key, rows: [row] });
  }
  return groups;
}

const SKELETON_ROW_COUNT = 10;
const SKELETON_COLUMN_WIDTHS = ["120px", "100px", "100px", "60px", "60px"];

export const ManagementDataTable = ({
  columns,
  rows,
  sortColumn = null,
  sortDirection = null,
  onSort,
  onLoadMore,
  hasMore = false,
  loadMoreLabel = "Load More",
  isLoadingMore = false,
  onRowClick,
  grouping,
}: ManagementDataTableProps) => {
  const groups = toGroups(rows, grouping);

  const handleHeaderClick = (columnKey: string, sortable?: boolean) => {
    if (sortable && onSort) {
      onSort(columnKey);
    }
  };

  return (
    <TableContainer>
      <Table>
        {columns.some((c) => c.width) && (
          <colgroup>
            {columns.map((column) => (
              <col key={column.key} style={column.width ? { width: column.width } : undefined} />
            ))}
          </colgroup>
        )}
        <TableHeader>
          <TableHeaderRow>
            {columns.map((column) => (
              <TableHeaderCell
                key={column.key}
                className={clsx(column.sortable && sortableHeaderCell)}
                onClick={() => handleHeaderClick(column.key, column.sortable)}
              >
                {column.label}
                {column.sortable && <SortIcon direction={sortDirection} isActive={sortColumn === column.key} />}
              </TableHeaderCell>
            ))}
          </TableHeaderRow>
        </TableHeader>
        {groups.map((group, groupIndex) => (
          <TableBody key={group.key === null ? "rows" : `${groupIndex}-${group.key}`}>
            {group.key !== null && grouping && (
              <GroupHeaderRow>
                <GroupHeaderCell scope="rowgroup" colSpan={columns.length}>
                  <GroupLabel>{grouping.labelOf(group.key)}</GroupLabel>
                </GroupHeaderCell>
              </GroupHeaderRow>
            )}
            {group.rows.map((row) => (
              <TableRow
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row.id) : undefined}
                style={{ cursor: onRowClick ? "pointer" : "default" }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={`${row.id}-${column.key}`}
                    className={clsx({
                      [stringCell]: column.type === "string",
                      [numberCell]: column.type === "number",
                    })}
                    onClick={(e) => {
                      if (column.type === "custom") {
                        e.stopPropagation();
                      }
                    }}
                  >
                    {column.renderCell ? (
                      column.renderCell(row)
                    ) : (
                      <CellValue value={row[column.key]} type={column.type} />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {isLoadingMore &&
              groupIndex === groups.length - 1 &&
              Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
                <TableRow key={`skeleton-${rowIndex}`}>
                  {columns.map((column, colIndex) => (
                    <TableCell key={`skeleton-${rowIndex}-${column.key}`}>
                      <CellSkeleton style={{ width: SKELETON_COLUMN_WIDTHS[colIndex] || "80px" }} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        ))}
      </Table>
      {hasMore && onLoadMore && !isLoadingMore && (
        <LoadMoreButton onClick={onLoadMore} label={loadMoreLabel} isLoading={isLoadingMore} />
      )}
    </TableContainer>
  );
};
